import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { StripHtmlPipeUtility } from '@shared/pipes/strip-html.pipe';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, of, switchMap } from 'rxjs';
import { Dictionary, RedirectItem } from 'tsui';
import moment from 'moment';
import formatMoney from 'accounting-js/lib/formatMoney.js';
import formatNumber from 'accounting-js/lib/formatNumber.js';
import { TsTableUtils } from 'tsui/lib/ts-table/ts-table.utils';
import { hasValue } from '@shared/Utils/common.utils';

export type NestedValueCellRenderedParams = ICellRendererParams & {
  nestedKeyName: string;
  recordType: string;
  relatedToRedirectConfig: Dictionary<RedirectItem>;
};

type SelectLink = {
  id: string;
  url: Observable<string>;
  label: string;
  deferred?: boolean;
  queryParams?: Dictionary<string>;
};

@Component({
  standalone: true,
  imports: [RouterModule, AsyncPipe, CommonModule],
  template: `
    @for (item of items; track item.id; let last = $last) {
      @if (item.deferred) {
        <a (click)="deferredRedirect(item, $event)" [class.inline-block]="params?.colDef?.cellDataType === 'textarea'">
          <ng-container *ngTemplateOutlet="labelTemplate; context: { label: item.label }" />
        </a>
      } @else {
        <a
          [routerLink]="item.url | async"
          [queryParams]="item.queryParams"
          [class.inline-block]="params?.colDef?.cellDataType === 'textarea'"
        >
          <ng-container *ngTemplateOutlet="labelTemplate; context: { label: item.label }" />
        </a>
      }
      {{ last ? null : ', ' }}
    }

    <ng-template #labelTemplate let-label="label">
      @if (params?.colDef?.cellDataType === 'textarea') {
        <span [innerHTML]="sanitizeHtml(label)"></span>
      } @else {
        <span>{{ label }}</span>
      }
    </ng-template>
  `,
  styles: [
    `
      a {
        text-decoration: underline;
        cursor: pointer;
      }
      a:hover {
        color: var(--primary-color);
      }
    `,
  ],
})
export class TableRecordRendererComponent implements ICellRendererAngularComp {
  items: any[] = [];
  params: NestedValueCellRenderedParams;
  urlPattern: RedirectItem;

  constructor(
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
  ) {}

  agInit(params: NestedValueCellRenderedParams): void {
    this.params = params;
    const { nestedKeyName, valueFormatted, value } = params;

    if (!this.params.data.isCategory && this.params.colDef.field.includes('.')) {
      this.handleNested();
    } else {
      if (Array.isArray(value)) {
        this.items = value.map((item, index) =>
          this.nestedValueToLink(item, Array.isArray(valueFormatted) ? valueFormatted[index] : null),
        );
      } else if (value) {
        this.items = [this.nestedValueToLink(value, valueFormatted)];
      }
    }
  }

  refresh(params: NestedValueCellRenderedParams): boolean {
    this.agInit(params);
    return true;
  }

  deferredRedirect(item: SelectLink, event: MouseEvent): void {
    item.url.subscribe((url) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(url);
      }
    });
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }

  private nestedValueToLink(nestedValue: { id: string; [key: string]: any }, formattedLabel?: string): SelectLink {
    const id = nestedValue.id;
    let label = formattedLabel || nestedValue[this.params.nestedKeyName] || null;

    if (this.params.nestedKeyName === 'body' && label) {
      label = StripHtmlPipeUtility.transform(label);
    } else if (label && typeof label === 'string' && label.includes('|')) {
      label = label.split('|')[1].trim();
    }

    const redirectConfig = this.params.relatedToRedirectConfig[this.params.recordType];
    let url: Observable<string> = null;

    if (redirectConfig) {
      url = redirectConfig.route(Number(id)).pipe(switchMap((pattern) => of(pattern.replace(':id', id))));
    }

    return {
      id,
      url,
      label,
      deferred: redirectConfig?.deferred,
      queryParams: redirectConfig?.getQueryParams?.(Number(id)),
    };
  }

  private handleNested(): void {
    const nestedValue = this.params.data[this.params.colDef.field.split('.')[0]];

    if (Array.isArray(nestedValue)) {
      this.items = nestedValue.map((item) => this.nestedValueToLink(item, this.formatNestedRecordLabel(item)));
    } else if (nestedValue) {
      this.items = [this.nestedValueToLink(nestedValue, this.formatNestedRecordLabel(nestedValue))];
    }
  }

  private formatNestedRecordLabel(record: { [key: string]: any }): string | undefined {
    let formattedLabel: string | undefined;
    let value: any = null;

    switch (this.params.colDef.context.type) {
      case 'select':
        formattedLabel = (
          this.params.colDef.context.meta.options ||
          this.params.colDef.cellRendererParams?.options[this.params.colDef.context.meta.option_name]
        ).find((option) => option.id == record[this.params.nestedKeyName])?.name;
        break;

      case 'date':
      case 'datetime':
        formattedLabel = record[this.params.nestedKeyName]
          ? moment(record[this.params.nestedKeyName]).format('MM/DD/YYYY')
          : '-';
        break;

      case 'money':
        value = record[this.params.nestedKeyName];
        value = value !== null && value !== undefined ? parseFloat(value) : null;
        formattedLabel =
          value !== null ? formatMoney(value, { precision: this.params.colDef.context.meta?.scale || 0 }) : '';
        break;

      case 'number':
        value = record[this.params.nestedKeyName];

        // If scale is defined, format with precision like decimal type
        if (this.params.colDef.context.meta?.scale !== null && this.params.colDef.context.meta?.scale !== undefined) {
          value = hasValue(value) ? parseFloat(value) : null;
          formattedLabel =
            value !== null ? formatNumber(value, { precision: this.params.colDef.context.meta.scale }) : '';
          break;
        }

        formattedLabel = TsTableUtils.formatNumberWithFieldRules(value, this.params.colDef.context);
        break;

      case 'decimal':
        value = record[this.params.nestedKeyName];

        if (!this.params.colDef.context.meta?.scale) {
          formattedLabel = value;
        }

        value = hasValue(value) ? parseFloat(value) : null;

        formattedLabel =
          value !== null ? formatNumber(value, { precision: this.params.colDef.context.meta?.scale || 0 }) : '';

        break;

      case 'percent':
        value = record[this.params.nestedKeyName];

        formattedLabel = hasValue(value)
          ? `${formatNumber(value, { precision: this.params.colDef.context.meta?.scale || 0 })}%`
          : '';

        break;
    }

    return formattedLabel;
  }
}
